import React, {useMemo} from 'react'
import {Box, Text} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {useProducts} from '@salesforce/commerce-sdk-react'
import ProductTile, {Skeleton as ProductTileSkeleton} from '../../../../product-tile'
import {getGDIHideStyles, pickGDIDomProps} from '../../utils/gdi-styles'
import {getExpandedFromContentArea} from '../../utils/gdi-content-area'
import {useGDIProductToggles} from '../../utils/gdi-link-context'
import {isPreviewOrDev} from '../../../utils/previewEnv'

const GDIProductTileBlock = ({
    HideBlockOn,
    ProductId,
    OverrideImage,
    Product,
    IsWishlistItem,
    ...props
}) => {
    const productToggles = useGDIProductToggles()
    const hideStyles = getGDIHideStyles(HideBlockOn)
    if (hideStyles.doNotLoadComponent) return null

    if (!ProductId && !Product) return null

    /* When a pre-fetched product is supplied (e.g. from a Vicky-driven
     * carousel), skip the SFCC fetch entirely. The hook is still declared so
     * hook order stays stable, but `enabled` is false. */
    const {
        data: productsData,
        isLoading,
        error
    } = useProducts(
        {
            parameters: {
                ids: ProductId,
                perPricebook: true,
                allImages: true,
                expand: ['promotions', 'variations', 'prices', 'images']
            }
        },
        {enabled: !!ProductId && !Product}
    )

    const overrideImageBlock = getExpandedFromContentArea(OverrideImage)

    const product = useMemo(() => {
        const p = Product || productsData?.data?.[0]
        if (!p) return null

        if (p.imageGroups && Array.isArray(p.imageGroups)) {
            p.imageGroups = p.imageGroups.map((group) => ({
                ...group,
                images: group.images?.map((img) => ({
                    ...img,
                    disBaseLink: img.disBaseLink || img.link || '',
                    link: img.link || img.disBaseLink || ''
                }))
            }))
        }

        // Apply override image from CMS if provided
        const overrideUrl = overrideImageBlock?.Url || overrideImageBlock?.Image?.Expanded?.Url
        if (overrideUrl && p.imageGroups?.[0]?.images?.[0]) {
            p.imageGroups[0].images[0] = {
                ...p.imageGroups[0].images[0],
                disBaseLink: overrideUrl,
                link: overrideUrl
            }
        }

        return p
    }, [Product, productsData, overrideImageBlock])

    /* Skip the loading state when a pre-fetched product is supplied — that
     * branch never enables the SFCC fetch. */
    if (isLoading && !Product) {
        return (
            <Box data-block-type="GDIProductTileBlock" sx={hideStyles.sx}>
                <ProductTileSkeleton />
            </Box>
        )
    }

    if (error && !Product) {
        // Diagnostic only in dev/preview; hide the tile in production.
        if (!isPreviewOrDev()) return null
        return (
            <Box
                data-block-type="GDIProductTileBlock"
                sx={hideStyles.sx}
                p={4}
                bg="red.50"
            >
                <Text color="red.500" fontSize="sm">
                    Product fetch error: {error.message}
                </Text>
            </Box>
        )
    }

    if (!product) {
        // Diagnostic only in dev/preview; hide the tile in production.
        if (!isPreviewOrDev()) return null
        return (
            <Box
                data-block-type="GDIProductTileBlock"
                sx={hideStyles.sx}
                p={4}
                bg="yellow.50"
            >
                <Text fontSize="sm">Product not found: {ProductId || Product?.id}</Text>
            </Box>
        )
    }

    return (
        <Box
            data-block-type="GDIProductTileBlock"
            data-product-id={ProductId || product.id}
            sx={hideStyles.sx}
            {...pickGDIDomProps(props)}
        >
            <ProductTile
                product={product}
                enableFavourite={true}
                showQV={true}
                /* Always mount swatches so the colorSwatches toggle (a
                 * responsive `display` value) drives final visibility.
                 * Spec default for the toggle is `No`, so the swatches stay
                 * hidden when the author leaves the field at "Default". */
                showSwatches={true}
                responsiveDisplay={productToggles}
                forceTileMode={productToggles?.forceTileMode}
                isWishlistPage={!!IsWishlistItem}
                /* Where the host block owns the horizontal swipe, the tile's
                 * media row can't be scrolled to — so don't build it. */
                disableImageScroll={productToggles?.tileMediaScrollDisabled}
                allowQuickView={!!IsWishlistItem}
                hideSize={!!IsWishlistItem}
            />
        </Box>
    )
}

GDIProductTileBlock.propTypes = {
    HideBlockOn: PropTypes.string,
    ProductId: PropTypes.string,
    OverrideImage: PropTypes.array,
    Product: PropTypes.object,
    IsWishlistItem: PropTypes.bool,
    GDIProductTileBlockAudienceTargeting: PropTypes.object
}

export default GDIProductTileBlock
